import IconTooltip from 'components/ui/base/tooltip/IconTooltip';
import { TextSmall } from 'components/ui/base/typography';
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import { ChangeEvent, FC, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ErrorDiv,
    ErrorLabel,
    FieldWrapper,
    FlexDiv,
    FormItem,
    InputLabel,
    InputLabelRequired,
    InputSection,
    LabelDiv,
    MetaSection,
    RefDiv,
    ViewOnlyLabel,
} from '../formInput/DeclarationInput.styles';
import { CheckboxInputDiv, SourceIconWrapper, StyledCheckbox } from './DeclarationCheckbox.styles';
import { CheckboxProps } from 'antd';
import { TemplateCheckboxes } from '../../template/TemplateCheckbox';
import { useTemplateContext } from '../../template/TemplateContext';
import useFieldTemplateMetaData from '../../template/useTemplateViewEditControls';
import useNonAmendableField from 'views/declarations/utils/useNonAmendableField';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useRegisterField } from '../../template/CardContext';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import useDeclarations from 'hooks/useDeclarations';
import { kebabCase } from 'lodash';
import { StyledInfoCircleFilled } from 'views/declarations/declaration-tabs/declaration-form/Form.styles';
import { useDeclarationValueSource } from '../source/DeclarationSourceContext';
import DeclarationValueSourceIcon from '../source/DeclarationValueSourceIcon';

export interface Props extends CheckboxProps {
    label?: string;
    tooltip?: string;
    icon?: ReactNode;
    refNumber?: string;
    children?: ReactNode;
    fieldProps: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
    disabled?: boolean;
    required?: boolean;
    viewOnly?: boolean;
    condensed?: boolean;
    hidden?: boolean;
}

export interface IconTooltipProps {
    tooltip?: string;
    icon?: ReactNode;
}

const DeclarationCheckbox: FC<Props> = ({
    label,
    refNumber,
    tooltip,
    fieldMeta: fieldMetaProp,
    fieldProps: fieldPropsProp,
    fieldHelper: fieldHelpersProp,
    viewOnly: viewOnlyProp,
    required,
    condensed,
    hidden,
    children,
    ...checkboxProps
}) => {
    const { t } = useTranslation('common');
    const { templateFormik, form, template, isViewOnly: isTemplateViewOnly } = useTemplateContext();
    const { isEditable, isInvisible } = useFieldTemplateMetaData(fieldPropsProp?.name ?? checkboxProps.name);
    const isNonAmendable = useNonAmendableField(fieldPropsProp?.name ?? checkboxProps.name);
    const { declaration } = useDeclarations();
    const outletContext = useOutletContext<{
        amendment?: boolean;
    }>();
    const location = useLocation();
    const inViewOnly = location.pathname.includes('view-only');

    const { fieldProps, fieldMeta } = useMemo(() => {
        if (template && templateFormik && form) {
            const f = getFormikProps(`${form}.defaults.${fieldPropsProp?.name}`, templateFormik);
            return { ...f, fieldHelpers: f.fieldHelper };
        }

        return { fieldProps: fieldPropsProp, fieldMeta: fieldMetaProp, fieldHelpers: fieldHelpersProp };
    }, [template, templateFormik, form, fieldPropsProp, fieldMetaProp, fieldHelpersProp]);

    const viewOnly = useMemo(() => {
        if (template) return isTemplateViewOnly;
        if (outletContext?.amendment) return false;
        return viewOnlyProp || !isEditable || inViewOnly || declaration?.archived;
    }, [
        template,
        isTemplateViewOnly,
        outletContext?.amendment,
        viewOnlyProp,
        isEditable,
        inViewOnly,
        declaration?.archived,
    ]);

    useRegisterField({ path: fieldPropsProp?.name ?? checkboxProps.name, required, hidden });

    const valueSource = useDeclarationValueSource(fieldPropsProp?.name ?? checkboxProps.name, fieldMeta);

    const sourceIcon = useMemo(
        () => (valueSource ? <DeclarationValueSourceIcon {...valueSource} /> : undefined),
        [valueSource]
    );

    const handleLabelClick = useCallback(() => {
        // The label toggles the box directly, so it has to honour the same locks the input does.
        if (isInvisible || viewOnly || isNonAmendable) return;
        const checked = checkboxProps.checked ?? checkboxProps.value ?? fieldProps.value;
        fieldProps.onChange({
            target: { name: fieldProps.name, type: 'checkbox', checked: !checked },
        } as ChangeEvent<HTMLInputElement>);
    }, [isInvisible, viewOnly, isNonAmendable, checkboxProps.checked, checkboxProps.value, fieldProps]);

    const handleTemplateCheckboxChange = useMemo(() => {
        return checkboxProps.onChange
            ? (value: any) =>
                  checkboxProps.onChange!({
                      target: { value, name: checkboxProps.name ?? '' },
                  } as any)
            : undefined;
    }, [checkboxProps.name, checkboxProps.onChange]);

    const render = useMemo(
        () => (
            <FieldWrapper $viewOnly={viewOnly} $template={template}>
                {viewOnly ? (
                    <>
                        <MetaSection>
                            <ViewOnlyLabel>{label && label}:</ViewOnlyLabel>
                        </MetaSection>
                        <InputSection>
                            {template && <TemplateCheckboxes disabled fieldPath={fieldPropsProp?.name} />}
                            <TextSmall data-testid={kebabCase(label || fieldPropsProp?.name)}>
                                {fieldProps.value ? 'Yes' : 'No'}
                            </TextSmall>
                        </InputSection>
                    </>
                ) : (
                    <>
                        <MetaSection>
                            <FlexDiv>
                                <LabelDiv condensed={condensed}>
                                    {label && <InputLabel>{label}</InputLabel>}
                                    {required && <InputLabelRequired right>*</InputLabelRequired>}
                                    {refNumber && <RefDiv>{refNumber}</RefDiv>}
                                    {tooltip && tooltip.length > 0 && (
                                        <IconTooltip
                                            title={label}
                                            tooltip={tooltip ?? t('defaultTooltip')}
                                            icon={<StyledInfoCircleFilled />}
                                        />
                                    )}
                                </LabelDiv>
                            </FlexDiv>
                        </MetaSection>
                        <InputSection>
                            <FormItem validateStatus={fieldMeta?.error && !!fieldMeta?.touched ? 'error' : ''}>
                                <CheckboxInputDiv>
                                    <StyledCheckbox
                                        checked={checkboxProps.checked ?? checkboxProps.value ?? fieldProps.value}
                                        data-testid={kebabCase(label ?? fieldPropsProp?.name)}
                                        {...fieldProps}
                                        {...checkboxProps}
                                        disabled={isInvisible || viewOnly || isNonAmendable || checkboxProps.disabled}
                                    />
                                    {children && (
                                        <span
                                            onClick={handleLabelClick}
                                            style={{ cursor: isInvisible || viewOnly ? 'default' : 'pointer' }}
                                        >
                                            {children}
                                        </span>
                                    )}
                                    <SourceIconWrapper>{sourceIcon}</SourceIconWrapper>
                                    {template && (
                                        <TemplateCheckboxes
                                            disabled={required}
                                            fieldPath={fieldPropsProp?.name ?? checkboxProps.name}
                                            onChange={handleTemplateCheckboxChange}
                                            inCheckbox
                                        />
                                    )}
                                </CheckboxInputDiv>
                                <ErrorDiv
                                    error={!!fieldMeta?.error}
                                    touched={!!fieldMeta?.touched}
                                    condensed={condensed}
                                >
                                    {!!fieldMeta?.error && !!fieldMeta?.touched && (
                                        <ErrorLabel>{fieldMeta?.error}</ErrorLabel>
                                    )}
                                </ErrorDiv>
                            </FormItem>
                        </InputSection>
                    </>
                )}
            </FieldWrapper>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            fieldMeta?.error,
            fieldMeta?.touched,
            fieldMeta?.value,
            isInvisible,
            viewOnly,
            sourceIcon,
            children,
            handleLabelClick,
        ]
    );

    if (hidden || (isInvisible && !template)) return null;

    return render;
};

export default DeclarationCheckbox;
